import JwtDecode from "jwt-decode";

const TOKEN = "token";

/**
 * Profile fields this device has saved since the current token was minted.
 *
 * The app `user` is decoded from the JWT and from nothing else, and the JWT
 * bakes in `name` and `image` at sign-in. `PUT /users/edit/:id` saves the
 * profile but does not re-mint that token, so without this the app kept
 * showing the pre-save photo and name — not just until navigation, but across
 * full reloads, until a fresh sign-in or a Supabase token refresh happened to
 * land (P1-38).
 *
 * This is a *client-side* overlay on the decoded token, deliberately holding
 * only the two display fields a profile save can change. It is never a source
 * of authority: the server still owns the profile, and anything that mints a
 * new token drops the overlay (see `setTokenInLocalStorage`).
 */
const PROFILE_OVERLAY = "profileOverlay";

const asString = (value) => (value === null || value === undefined ? "" : String(value));

const pickName = (name) => ({
  first: asString(name.first),
  middle: asString(name.middle),
  last: asString(name.last),
});

const pickImage = (image) => ({
  url: asString(image.url),
  alt: asString(image.alt),
});

/**
 * Storage is not always readable (#209).
 *
 * A browser with site data blocked does not hand back an empty store — every
 * accessor *throws* SecurityError, and Chrome throws on reading the
 * `window.localStorage` property itself. That rules out a feature-detect
 * (`if (!window.localStorage)`), which would throw on the guard: only a
 * try/catch around the whole access works.
 *
 * These three collapse an unreachable store into the state this module already
 * produces for an empty one — nothing stored, so nobody is signed in — which is
 * the same shape `saveProfileOverlay` below, `CurrencyProvider`,
 * `OnboardingCoach` and `errorReporter` already use. Deliberately silent: a
 * blocked store is the visitor's own setting, not an error to report.
 */
const readItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const dropItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to clear when the store is unreachable — it never held anything.
  }
};

const clearProfileOverlay = () => dropItem(PROFILE_OVERLAY);

export const setTokenInLocalStorage = (encryptedToken) => {
  // A freshly minted token carries the server's current name and image, so it
  // supersedes whatever the overlay was holding. Dropping it here keeps the
  // overlay from outliving its usefulness — or from shadowing a newer value
  // saved on another device.
  clearProfileOverlay();
  // DELIBERATELY UNGUARDED, unlike the reads above (#209). This throw is caught
  // by the sign-in callers (useHandleUsers handleLogin/handleSignup,
  // AuthCallbackPage), which already turn it into a red "could not sign in"
  // snack. Swallowing it here would strictly worsen that: the token would live
  // in memory only, getUserFromLocalStorage would still answer null, and the
  // caller would show "Logged in successfully!" and navigate — whereupon
  // RequireAuth sees no user and bounces straight back to the login form. A
  // green lie that loops forever is worse than an honest error.
  return localStorage.setItem(TOKEN, encryptedToken);
};

const readProfileOverlay = () => {
  const raw = readItem(PROFILE_OVERLAY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed._id) return null;
    return parsed;
  } catch {
    clearProfileOverlay();
    return null;
  }
};

/**
 * Merge a saved-profile overlay onto a decoded user. Pure, and exported so the
 * provider can apply the exact same merge to its in-memory `user` that a later
 * reload will apply to the decoded token — the two halves cannot drift.
 */
export const applyProfileOverlay = (user, overlay) => {
  if (!user || !overlay) return user;
  // Scoped to the account it was saved for, so a sign-out/sign-in race can
  // never paint one collector's photo onto another's session.
  if (asString(overlay._id) !== asString(user._id)) return user;
  return {
    ...user,
    ...(overlay.name ? { name: { ...user.name, ...overlay.name } } : {}),
    ...(overlay.image ? { image: { ...user.image, ...overlay.image } } : {}),
  };
};

/**
 * Record the profile fields a save just committed. Returns the normalized
 * overlay (so the caller can apply the same merge in memory) or null when
 * there is nothing worth recording.
 */
export const saveProfileOverlay = (profile) => {
  if (!profile || !profile._id) return null;
  const overlay = { _id: asString(profile._id) };
  if (profile.name && typeof profile.name === "object") overlay.name = pickName(profile.name);
  if (profile.image && typeof profile.image === "object") overlay.image = pickImage(profile.image);
  if (!overlay.name && !overlay.image) return null;
  try {
    localStorage.setItem(PROFILE_OVERLAY, JSON.stringify(overlay));
  } catch {
    // A full/blocked storage costs us the reload half, not the save. Still
    // return the overlay so the on-screen avatar updates immediately.
  }
  return overlay;
};

export const getUserFromLocalStorage = () => {
  const token = readItem(TOKEN);
  if (!token) return null;
  try {
    const user = JwtDecode(token);
    if (user.exp && user.exp * 1000 < Date.now()) {
      dropItem(TOKEN);
      clearProfileOverlay();
      return null;
    }
    return applyProfileOverlay(user, readProfileOverlay());
  } catch {
    dropItem(TOKEN);
    clearProfileOverlay();
    return null;
  }
};

// analytics.js's per-tab session id. Spelled out here rather than imported:
// analytics.js imports getToken from this file.
const ANALYTICS_SESSION_KEY = "analytics_session_id";

export const removeToken = () => {
  clearProfileOverlay();
  // A sign-out ends the analytics session too, so the next account to sign in
  // on this tab never has its events stitched onto the previous person's.
  try {
    sessionStorage.removeItem(ANALYTICS_SESSION_KEY);
  } catch {
    // Storage blocked: there is no session id to carry over either.
  }
  return dropItem(TOKEN);
};

export const getToken = () => readItem(TOKEN);
